// ===============================
// FILE 1: src/lib/createInvoiceOnly.ts  (או איפה שזה אצלך)
// ===============================

export type AmountUnit = "ils" | "agorot" | "auto";

export interface CreateInvoiceOnlyParams {
  businessId: string;
  customerId: string;
  appointmentId?: string;
  terminalId?: string;

  /**
   * amount = סכום סופי שהלקוח שילם (GROSS)
   * מומלץ: ILS
   */
  amount: number;
  tipAmount?: number;
  amountUnit?: AmountUnit;

  serviceDescription: string;
  customerName: string;
  customerEmail: string;
  customerPhone?: string;
  customerAddress?: string;

  /**
   * cash | bit | bank_transfer | credit_card
   */
  paymentType?: string;

  /**
   * פטור  -> includeVat=false
   * מורשה -> includeVat=true
   */
  includeVat?: boolean;
  vatRate?: number;

  documentRemarks?: string;

  Uid?: string;
  quantity?: number;
  catalogId?: string;
  extraRecipients?: string;
  paymentDueDate?: string;
  incomeId?: number;
  customerBusinessPhone?: string;

  trxRecordId?: string;
}

function safeString(v: unknown) {
  return String(v ?? "").trim();
}

function toNum(v: unknown, fallback = 0) {
  const s = String(v ?? "").trim();
  if (!s) return fallback;
  const n = Number(s.replace(",", "."));
  return Number.isFinite(n) ? n : fallback;
}

function validateParams(p: CreateInvoiceOnlyParams) {
  if (!p.businessId) throw new Error("חסר businessId");
  if (!p.customerId) throw new Error("חסר customerId");
  if (p.amount == null) throw new Error("חסר amount");
  if (!p.customerName) throw new Error("חסר customerName");
  if (!p.serviceDescription) throw new Error("חסר serviceDescription");

  const amount = toNum(p.amount, NaN);
  if (!Number.isFinite(amount) || amount <= 0) throw new Error("amount לא תקין");

  const tip = toNum(p.tipAmount ?? 0, NaN);
  if (!Number.isFinite(tip) || tip < 0) throw new Error("tipAmount לא תקין");
}

/**
 * createInvoiceOnly
 * ✅ שולח סכום GROSS
 * ✅ includeVat חייב להגיע נכון לפי העסק (פטור/מורשה)
 */
export async function createInvoiceOnly(
  params: CreateInvoiceOnlyParams,
  accessToken: string,
  options?: {
    endpoint?: string;
    debug?: boolean;
  }
) {
  if (!accessToken) throw new Error("אין access_token, המשתמש לא מחובר");

  validateParams(params);

  const endpoint =
    options?.endpoint ??
    "https://nkuqcyelxgyihrxyvitb.supabase.co/functions/v1/create-invoice";

  // 🔒 ננעלים על ILS כדי למנוע בלבולי אגורות/ש״ח
  const forcedUnit: AmountUnit = "ils";

  const includeVat =
    typeof params.includeVat === "boolean" ? params.includeVat : undefined;

  // vatRate נשלח רק אם includeVat=true
  const vatRateToSend =
    includeVat === true
      ? (params.vatRate != null ? toNum(params.vatRate, 0.18) : undefined)
      : undefined;

  const payload: Record<string, any> = {
    businessId: params.businessId,
    customerId: params.customerId,
    appointmentId: params.appointmentId ?? undefined,
    terminalId: params.terminalId ?? undefined,

    // ✅ שולחים מספרים, לא מחרוזות
    amount: toNum(params.amount, 0),
    tipAmount: toNum(params.tipAmount ?? 0, 0),
    amountUnit: forcedUnit,

    serviceDescription: safeString(params.serviceDescription),
    customerName: safeString(params.customerName),
    customerEmail: safeString(params.customerEmail),
    customerPhone: safeString(params.customerPhone ?? ""),
    customerAddress: safeString(params.customerAddress ?? "לא צויין"),

    paymentType: safeString(params.paymentType ?? "cash"),

    includeVat,              // ✅ חשוב: מגיע לפונקציה
    vatRate: vatRateToSend,  // ✅ נשלח רק אם includeVat=true

    documentRemarks: safeString(params.documentRemarks ?? "תודה שבחרת בנו!"),

    quantity: params.quantity != null ? toNum(params.quantity, 1) : undefined,
    catalogId: safeString(params.catalogId ?? ""),
    extraRecipients: safeString(params.extraRecipients ?? ""),
    paymentDueDate: safeString(params.paymentDueDate ?? ""),
    incomeId: params.incomeId != null ? Number(params.incomeId) : undefined,
    customerBusinessPhone: safeString(params.customerBusinessPhone ?? ""),

    Uid: safeString(params.Uid ?? ""),
    trxRecordId: safeString(params.trxRecordId ?? params.Uid ?? ""),
  };

  if (options?.debug) {
    console.log("[createInvoiceOnly] payload SAFE:", payload);
  }

  const response = await fetch(endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(payload),
  });

  const text = await response.text();
  let json: any = null;
  try {
    json = JSON.parse(text);
  } catch {
    json = { raw: text };
  }

  if (!response.ok) {
    throw new Error(
      `create-invoice HTTP ${response.status} ${response.statusText} - ${text}`
    );
  }

  const ok =
    json?.success === true ||
    (json?.raw &&
      String(json.raw.result) === "200" &&
      String(json.raw.description).toLowerCase() === "success");

  if (!ok) throw new Error(json?.error || "הפקת החשבונית נכשלה");

  return {
    success: true,
    invoiceLink: json?.invoiceLink ?? json?.document_link ?? null,
    invoiceNumber: json?.invoiceNumber ?? json?.document_id ?? null,
    vatModeUsed: json?.vatModeUsed ?? null,
    debug: json?.debug ?? null,
    raw: json?.raw ?? json,
  };
}
